import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, FadeIn, MaskedLines } from "@/components/Motion";
import { SERVICES } from "@/data/site";

function PageHero() {
  return (
    <section className="relative overflow-hidden bg-navy-deep pb-20 pt-40 lg:pb-28 lg:pt-52">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="absolute -right-24 top-0 h-full w-1/2 bg-[radial-gradient(ellipse_at_top_right,rgba(196,164,106,0.12),transparent_60%)]"
      />
      <div className="container-x relative max-w-3xl">
        <FadeIn y={16}>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Our Services
          </p>
        </FadeIn>
        <h1 className="mt-7 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
          <MaskedLines
            delay={0.3}
            lines={[
              <span key="s1">Professional Services &amp;</span>,
              <span key="s2">
                Enterprise Technology,{" "}
                <em className="font-medium italic text-gold">Designed to Scale</em>
              </span>,
            ]}
          />
        </h1>
        <FadeIn delay={0.9}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            Since 2002, SPA has specialized in business software reselling,
            ERP implementation, customization, training and support —
            complemented by management consultancy, bookkeeping, auditing,
            accounting, tax and financial services for organizations across
            Nigeria and West Africa.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function GroupHeader({ eyebrow, title, first = false }) {
  return (
    <div className={`container-x ${first ? "" : ""} max-w-none`}>
      <Reveal className="max-w-3xl pb-14 pt-4 lg:pb-16">
        <p className="eyebrow flex items-center gap-4">
          <span aria-hidden="true" className="h-px w-10 bg-gold" />
          {eyebrow}
        </p>
        <h2 className="mt-5 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
          {title}
        </h2>
      </Reveal>
    </div>
  );
}

function ServiceBlock({ service, index }) {
  const isNavy = service.theme === "navy";
  const isGrey = service.theme === "grey";
  const flip = index % 2 === 1;
  return (
    <section
      id={service.anchor}
      data-testid={`service-section-${service.id}`}
      className={`relative scroll-mt-24 py-20 lg:py-24 ${
        isNavy
          ? "overflow-hidden bg-navy-deep"
          : isGrey
            ? "bg-mist"
            : "bg-white"
      }`}
    >
      <span
        id={service.id}
        aria-hidden="true"
        className="absolute top-0 left-0 h-0 w-0 scroll-mt-24"
      />
      {isNavy && <div className="grain absolute inset-0" aria-hidden="true" />}
      <div
        className={`container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20 ${
          flip ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        <Reveal>
          <figure className="group relative">
            <span
              aria-hidden="true"
              className={`absolute hidden h-full w-full border border-gold/40 sm:block ${
                flip ? "-left-4 -top-4" : "-right-4 -top-4"
              }`}
            />
            <div className="relative overflow-hidden">
              <img
                src={service.image}
                alt={service.alt}
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
          </figure>
        </Reveal>
        <Reveal delay={0.12}>
          <div>
            <span
              className={`font-serif text-lg ${isNavy ? "text-gold" : "text-gold"}`}
            >
              {service.number}
            </span>
            <h3
              className={`mt-4 font-serif text-3xl font-semibold leading-[1.14] sm:text-4xl ${
                isNavy ? "text-white" : "text-navy"
              }`}
            >
              {service.title}
            </h3>
            <p
              className={`mt-6 text-base leading-relaxed sm:text-lg ${
                isNavy ? "text-white/70" : "text-charcoal/70"
              }`}
            >
              {service.description}
            </p>
            <span
              aria-hidden="true"
              className="mt-8 block h-px w-16 bg-gold"
            />
            <p
              className={`mt-8 text-[11px] font-semibold uppercase tracking-[0.24em] ${
                isNavy ? "text-white/50" : "text-charcoal/45"
              }`}
            >
              Illustrative Capabilities
            </p>
            <ul
              className={`mt-5 space-y-3 text-[15px] ${
                isNavy ? "text-white/75" : "text-charcoal/75"
              }`}
            >
              {service.capabilities.map((cap) => (
                <li key={cap} className="flex items-start gap-4">
                  <span
                    aria-hidden="true"
                    className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-gold"
                  />
                  {cap}
                </li>
              ))}
            </ul>
            <Link
              to={`/contact?service=${encodeURIComponent(service.dropdown)}`}
              data-testid={`service-cta-${service.id}`}
              className={`mt-10 ${isNavy ? "btn-gold" : "btn-navy"}`}
            >
              Discuss Your Business Needs
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ErpBlock({ service }) {
  return (
    <section
      id={service.anchor}
      data-testid={`service-section-${service.id}`}
      className="relative scroll-mt-24 overflow-hidden bg-navy-deep py-20 lg:py-28"
    >
      <span
        id={service.id}
        aria-hidden="true"
        className="absolute top-0 scroll-mt-24"
      />
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="absolute -left-24 top-0 h-full w-1/2 bg-[radial-gradient(ellipse_at_top_left,rgba(196,164,106,0.14),transparent_60%)]"
      />
      <div className="container-x relative">
        <Reveal className="max-w-3xl">
          <span className="font-serif text-lg text-gold">{service.number}</span>
          <h3 className="mt-4 font-serif text-3xl font-semibold leading-[1.12] text-white sm:text-4xl lg:text-5xl">
            {service.title}
          </h3>
          <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-semibold uppercase leading-[1.9] tracking-[0.22em] text-gold sm:text-xs">
            {service.subheading}
          </p>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45">
            Serving organizations since 2002 — over two decades of experience
          </p>
          <span aria-hidden="true" className="mt-8 block h-px w-16 bg-gold" />
        </Reveal>

        <div className="mt-12 grid items-start gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
          <Reveal delay={0.05}>
            <div className="space-y-5 text-base leading-relaxed text-white/70 sm:text-lg">
              {service.longDescription.map((p) => (
                <p key={p.slice(0, 24)}>{p}</p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div>
              <figure className="group relative">
                <span
                  aria-hidden="true"
                  className="absolute -right-4 -top-4 hidden h-full w-full border border-gold/40 sm:block"
                />
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.alt}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
              </figure>
              <div
                className="mt-10 border border-gold/40 bg-white/5 p-8"
                data-testid="sage-expertise"
              >
                <p className="eyebrow-navy">{service.sage.title}</p>
                {service.sage.paragraphs.map((p) => (
                  <p
                    key={p.slice(0, 24)}
                    className="mt-4 text-[15px] leading-relaxed text-white/70"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <p className="mt-16 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/50">
            ERP Capabilities
          </p>
        </Reveal>
        <div className="mt-6 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {service.capabilities.map((cap) => (
            <div
              key={cap.title}
              className="bg-navy-deep p-7 transition-colors duration-500 hover:bg-navy"
            >
              <span aria-hidden="true" className="block h-px w-8 bg-gold" />
              <h4 className="mt-4 font-serif text-lg font-semibold leading-snug text-white">
                {cap.title}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                {cap.body}
              </p>
            </div>
          ))}
          <div
            aria-hidden="true"
            className="hidden bg-navy-deep lg:block"
          />
        </div>

        <Reveal delay={0.08}>
          <div className="mt-14">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/50">
              Related Enterprise Technology Capabilities
            </p>
            <ul className="mt-5 flex flex-wrap gap-2.5">
              {service.relatedCapabilities.map((cap) => (
                <li
                  key={cap}
                  className="rounded-full border border-white/15 px-4 py-2 text-[13px] text-white/70 transition-colors hover:border-gold/50 hover:text-gold"
                >
                  {cap}
                </li>
              ))}
            </ul>
            <Link
              to={`/contact?service=${encodeURIComponent(service.dropdown)}`}
              data-testid={`service-cta-${service.id}`}
              className="btn-gold mt-10"
            >
              Discuss Your Business Needs
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const t = setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          300
        );
        return () => clearTimeout(t);
      }
    }
    return undefined;
  }, [hash]);

  const erp = SERVICES.find((s) => s.group === "technology");
  const advisory = SERVICES.filter((s) => s.group === "advisory");

  return (
    <>
      <Seo
        title="ERP & Business Software Solutions, Sage 300 Implementation, Accounting, Audit & Tax Services in Nigeria | Steven Philips & Associates Limited"
        description="SPA is a business software reseller and ERP implementation specialist — Sage 300 ERP (formerly Sage Accpac) sales, implementation, customization, training and support — with management consultancy, bookkeeping, auditing, accounting, tax and financial services in Nigeria."
      />
      <PageHero />
      <div className="border-y border-navy/10 bg-white">
        <ErpBlock service={erp} />
        <GroupHeader
          first
          eyebrow="Service Group — Advisory & Financial"
          title="Professional & Financial Advisory"
        />
        {advisory.map((s, i) => (
          <ServiceBlock key={s.id} service={s} index={i} />
        ))}
      </div>
    </>
  );
}
